import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFamily } from '../Redux/Actions/IngredientActions';
import { getSuppliers } from '../Redux/Actions/SupplierActions';
import { addInvoice } from '../Redux/Actions/invoiceActions';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import './InvoiceForm.css';

const NUMERIC_KEYPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', '⌫'],
];

const NumericKeypadModal = ({ show, title, value, onConfirm, onCancel }) => {
  const [buffer, setBuffer] = useState('');

  useEffect(() => {
    if (show) setBuffer(value === undefined || value === null ? '' : String(value));
  }, [show, value]);

  const handleKey = (k) => {
    if (k === '⌫') {
      setBuffer((s) => s.slice(0, -1));
      return;
    }
    if (k === '.') {
      if (buffer.includes('.')) return;
      if (buffer === '') {
        setBuffer('0.');
        return;
      }
    }
    if (buffer === '0' && k !== '.') {
      setBuffer(k);
      return;
    }
    setBuffer((s) => s + k);
  };

  const handleClear = () => setBuffer('');

  const handleConfirm = () => {
    const v = buffer.trim() === '' ? '' : buffer.replace(/^0+(\d)/, '$1');
    onConfirm(v);
    onCancel();
  };

  if (!show) return null;

  return (
    <div className="numeric-keypad-overlay" onClick={onCancel}>
      <div className="numeric-keypad-modal" onClick={(e) => e.stopPropagation()}>
        <div className="numeric-keypad-header">
          <span className="numeric-keypad-title">{title}</span>
          <button type="button" className="numeric-keypad-close" onClick={onCancel} aria-label="Fermer">
            ×
          </button>
        </div>
        <div className="numeric-keypad-display">{buffer || '0'}</div>
        <div className="numeric-keypad-grid">
          {NUMERIC_KEYPAD_KEYS.map((row, i) => (
            <div key={i} className="numeric-keypad-row">
              {row.map((k) => (
                <button
                  key={k}
                  type="button"
                  className="numeric-keypad-key"
                  onClick={() => handleKey(k)}
                >
                  {k}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="numeric-keypad-actions">
          <button type="button" className="numeric-keypad-btn numeric-keypad-btn-clear" onClick={handleClear}>
            C
          </button>
          <button type="button" className="numeric-keypad-btn numeric-keypad-btn-ok" onClick={handleConfirm}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const InvoiceForm = () => {
  const dispatch = useDispatch();

  const suppliers = useSelector((state) => state.SupplierReducer?.suppliers || []);
  const families = useSelector((state) => state.IngredientReducer?.families || []);

  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [rows, setRows] = useState([
    { family: '', ingredient: '', quantity: '', unit: '', tva:'', tvaAmount: 0, price: '', total: 0, ingredients: [] }
  ]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [keypad, setKeypad] = useState({
    show: false,
    index: null,
    field: null,
    title: '',
  });

  // Charger toutes les localisations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get("/api/Locationspin/getAllLocations");
        setLocations(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Erreur lors du chargement des emplacements :", err);
        setLocations([]);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    dispatch(getSuppliers());
    dispatch(getFamily());
  }, [dispatch]);

  const openKeypad = (index, field, title) => {
    if (submitting) return;
    setKeypad({ show: true, index, field, title });
  };

  const closeKeypad = () => {
    setKeypad((k) => ({ ...k, show: false }));
  };

  const confirmKeypad = (value) => {
    if (keypad.index == null || !keypad.field) return;
    handleChange(keypad.index, keypad.field, value);
    closeKeypad();
  };

  const addRow = () => {
    setRows([
      ...rows,
      { family: '', ingredient: '', quantity: '', unit: '', tva:'', tvaAmount: 0, price: '', total: 0, ingredients: [] }
    ]);
  };

  const removeRow = (index) => {
    const newRows = [...rows];
    newRows.splice(index, 1);
    setRows(newRows);
  };

  const handleChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;

    if (field === 'family') {
      const selectedFamily = families.find((fam) => fam._id === value);
      newRows[index].ingredients = selectedFamily ? selectedFamily.ingredients : [];
      newRows[index].ingredient = '';
    }

    if (field === 'ingredient') {
      const selectedIngredient = newRows[index].ingredients.find((ing) => ing._id === value);
      if (selectedIngredient) {
        newRows[index].unit = selectedIngredient.unit;
      }
    }

    const quantity = parseFloat(newRows[index].quantity) || 0;
    const price = parseFloat(newRows[index].price) || 0;
    const tvaPercentage = parseFloat(newRows[index].tva) || 0;

    // Calculer le montant de la TVA
    const tvaAmount = quantity * price * (tvaPercentage / 100);
    newRows[index].tvaAmount = tvaAmount.toFixed(3);

    // Calcul du total TTC
    newRows[index].total = quantity * price + tvaAmount;
    newRows[index].priceTTC = (price + price * (tvaPercentage / 100)).toFixed(3);

    setRows(newRows);
  };

  const getTotalInvoice = () => {
    return rows.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);
  };

  const getTotalTVA = () => {
    return rows.reduce((sum, row) => sum + (parseFloat(row.tvaAmount) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validations
    if (!selectedSupplier) {
      alert("Veuillez sélectionner un fournisseur");
      setSubmitting(false);
      return;
    }
    if (!invoiceDate) {
      alert("Veuillez sélectionner une date");
      setSubmitting(false);
      return;
    }
    if (!selectedLocation) {
      alert("Veuillez sélectionner une localisation");
      setSubmitting(false);
      return;
    }
    if (rows.length === 0) {
      alert("Veuillez ajouter au moins un ingrédient");
      setSubmitting(false);
      return;
    }

    // Vérifier que tous les prix sont saisis
    const missingPrice = rows.some(row => !row.price || parseFloat(row.price) <= 0);
    if (missingPrice) {
      alert("Veuillez saisir le prix d'achat pour tous les ingrédients");
      setSubmitting(false);
      return;
    }

    const confirmMessage = `Êtes-vous sûr de vouloir valider cette facture ?\n\n` +
    `- Fournisseur: ${suppliers.find(s => s._id === selectedSupplier)?.companyName}\n` +
    `- Date: ${invoiceDate}\n` +
    `- Localisation: ${locations.find(l => l._id === selectedLocation)?.name}\n` +
    `- Nombre d'articles: ${rows.length}\n` +
    `- Montant total: ${getTotalInvoice().toFixed(3)} DT`;

  const isConfirmed = window.confirm(confirmMessage);
  
  if (!isConfirmed) {
    setSubmitting(false);
    return;
  }

  const invoiceData = {
    supplier: selectedSupplier,
    date: invoiceDate,
    localisation: selectedLocation,
    items: rows.map((row) => ({
      family: row.family,
      ingredient: row.ingredient,
      quantity: parseFloat(row.quantity) || 0,
      unit: row.unit,
      tva: parseFloat(row.tva) || 0,
      tvaAmount: parseFloat(row.tvaAmount) || 0,
      price: parseFloat(row.price) || 0,
      total: parseFloat(row.total) || 0,
    })),
    totalAmount: getTotalInvoice(),
  };

  try {
    // Appel de l'action Redux et ATTENTE du résultat
    const result = await dispatch(addInvoice(invoiceData));
    
    console.log("Résultat de l'action:", result);
    
    // Vérification EXPLICITE du résultat
    if (result?.success === true) {
      // Réinitialiser le formulaire après succès
      setSelectedSupplier('');
      setInvoiceDate('');
      setSelectedLocation('');
      setRows([{ family: '', ingredient: '', quantity: '', unit: '', tva: '', tvaAmount: 0, price: '', total: 0, ingredients: [] }]);
      alert("✅ Facture ajoutée avec succès !");
    } else {
      // Afficher l'erreur spécifique retournée par le serveur
      const errorMsg = result?.error || "Erreur lors de l'ajout de la facture";
      alert(`❌ ${errorMsg}`);
    }
  } catch (err) {
    console.error("Erreur lors de la soumission:", err);
    alert("❌ Une erreur inattendue s'est produite");
  } finally {
    setSubmitting(false);
  }
};

  return (
    <form className="invoice-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Nom du fournisseur :</label>
        <select
          value={selectedSupplier}
          onChange={(e) => setSelectedSupplier(e.target.value)}
          required
          disabled={submitting}
        >
          <option value="">-- Sélectionnez un fournisseur --</option>
          {suppliers.map((supplier) => (
            <option key={supplier._id} value={supplier._id}>
              {supplier.companyName}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Date :</label>
        <input
          type="date"
          value={invoiceDate}
          onChange={(e) => setInvoiceDate(e.target.value)}
          required
          disabled={submitting}
        />
      </div>

      {/* Ajout de la localisation */}
      <div className="form-group">
        <label>Localisation :</label>
        <select
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          required
          disabled={submitting}
        >
          <option value="">-- Sélectionnez un emplacement --</option>
          {locations.map((loc) => (
            <option key={loc._id} value={loc._id}>
              {loc.name}
            </option>
          ))}
        </select>
      </div>

      <table>
        <thead>
          <tr>
            <th>Famille</th>
            <th>Ingrédient</th>
            <th>Quantité</th>
            <th>Unité</th>
            <th>Prix d'achat HT</th>
            <th>TVA</th>
            <th>Montant TVA</th>
            <th>Total</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td>
                <select
                  value={row.family}
                  onChange={(e) => handleChange(index, 'family', e.target.value)}
                  required
                  disabled={submitting}
                >
                  <option value="">-- Sélectionnez une famille --</option>
                  {families.map((family) => (
                    <option key={family._id} value={family._id}>
                      {family.name}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={row.ingredient}
                  onChange={(e) => handleChange(index, 'ingredient', e.target.value)}
                  disabled={!row.family || submitting}
                  required
                >
                  <option value="">-- Sélectionnez un ingrédient --</option>
                  {row?.ingredients?.map((ingredient) => (
                    <option key={ingredient._id} value={ingredient._id}>
                      {ingredient.name}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <div className="invoice-numeric-cell">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={row.quantity}
                    onChange={(e) => handleChange(index, 'quantity', e.target.value)}
                    className="invoice-numeric-input"
                    required
                    disabled={submitting}
                    placeholder="0"
                  />
                  <button
                    type="button"
                    className="invoice-keypad-btn"
                    onClick={() => openKeypad(index, 'quantity', `Quantité · Ligne ${index + 1}`)}
                    disabled={submitting}
                    title="Ouvrir le clavier numérique"
                    aria-label="Clavier numérique"
                  >
                    ⌨
                  </button>
                </div>
              </td>
              <td>
                <Form.Select
                  value={row.unit}
                  onChange={(e) => handleChange(index, 'unit', e.target.value)}
                  required
                  disabled={submitting}
                >
                  <option value="">-- Choisissez l'unité --</option>
                  <option value="Kg">Kg</option>
                  <option value="Litre">Litre</option>
                  <option value="Pièces">Pièces</option>
                </Form.Select>
              </td>
              <td>
                <div className="invoice-numeric-cell">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={row.price}
                    onChange={(e) => handleChange(index, 'price', e.target.value)}
                    className="invoice-numeric-input"
                    placeholder="Prix HT"
                    required
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="invoice-keypad-btn"
                    onClick={() => openKeypad(index, 'price', `Prix HT · Ligne ${index + 1}`)}
                    disabled={submitting}
                    title="Ouvrir le clavier numérique"
                    aria-label="Clavier numérique"
                  >
                    ⌨
                  </button>
                </div>
              </td>
              <td>
                <Form.Select
                  value={row.tva}
                  onChange={(e) => handleChange(index, 'tva', e.target.value)}
                  required
                  disabled={submitting}
                >
                  <option value="">-- Choisissez TVA --</option>
                  <option value="0">0%</option>
                  <option value="7">7%</option>
                  <option value="13">13%</option>
                  <option value="19">19%</option>
                </Form.Select>
              </td>
              <td>
                {row.tvaAmount ? `${row.tvaAmount}` : '0.000'}
              </td>
              <td>{row.total ? row.total.toFixed(3) : '0.000'}</td>
              <td>
                {rows.length > 1 && (
                  <button 
                    type="button" 
                    onClick={() => removeRow(index)}
                    disabled={submitting}
                  >
                    Supprimer
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button type="button" onClick={addRow} disabled={submitting}>
        Ajouter une ligne
      </button>

      <div className="invoice-totals">
        <strong>Total TVA : {getTotalTVA().toFixed(3)} DT</strong>
        <br />
        <strong>Total de la facture : {getTotalInvoice().toFixed(3)} DT</strong>
      </div>

      <button type="submit" disabled={submitting}>
        {submitting ? "⏳ Enregistrement..." : "Valider la facture"}
      </button>

      <NumericKeypadModal
        show={keypad.show}
        title={keypad.title}
        value={keypad.index != null && keypad.field ? (rows[keypad.index]?.[keypad.field] ?? '') : ''}
        onConfirm={confirmKeypad}
        onCancel={closeKeypad}
      />
    </form>
  );
};

export default InvoiceForm;